import { faSync } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CSSProperties, FC, ReactNode } from 'react';
import { useState } from 'react';
import { Spacer } from 'src/components/layout/spacer';
import { useNow } from 'src/hooks/use-now';
import { cn } from 'src/utils/cn';

type Align = 'flex-start' | 'center' | 'flex-end';
type Justify = 'flex-start' | 'center' | 'flex-end';

export const Spinner: FC<{ className?: string; style?: CSSProperties }> = ({
  className,
  style,
}) => {
  return (
    <FontAwesomeIcon
      icon={faSync}
      aria-label="Loading"
      className={cn(`animate-spin`, className)}
      style={style}
    />
  );
};

type Props = {
  pending: boolean;
  align?: Align;
  justify?: Justify;
  px?: string;
  py?: string;
  children: ReactNode;
  'data-testid'?: string;
};

export const SpinnerContainer: FC<Props> = (props) => {
  const {
    pending,
    justify = 'center',
    align = 'center',
    px = '0rem',
    py = '0rem',
    children,
    'data-testid': testId,
  } = props;

  return (
    <div className="relative z-0">
      {pending && (
        <div
          className={`
            absolute z-1 flex size-full px-(--spinner-container-px)
            py-(--spinner-container-py)
          `}
          style={
            {
              justifyContent: justify,
              alignItems: align,
              '--spinner-container-px': px,
              '--spinner-container-py': py,
            } as CSSProperties
          }
          data-testid={testId}
        >
          <Spinner className={`flex items-center justify-center`} />
        </div>
      )}
      <div
        className={cn(
          `transition-opacity duration-300`,
          pending && `opacity-40`
        )}
      >
        {children}
      </div>
    </div>
  );
};

const rotateAnimationDurationInMS = 800;

export const PageSpinner: FC = () => {
  const now = useNow();
  // explanation and sandbox for debugging: https://twitter.com/PipoPeperoni/status/1588433621018611714
  const [delay] = useState(() => now % rotateAnimationDurationInMS);
  return (
    <div className="grid grid-flow-col items-center justify-center">
      <Spacer />
      <Spinner
        className="flex items-center justify-center"
        style={{ animationDelay: `-${delay}ms` } as CSSProperties}
      />
      <Spacer />
    </div>
  );
};
